import type { StatusKey } from '@/data/types/contract-status'
import { statusMap } from '@/utils/statusMaps'
import type { Status } from '@/utils/statusMaps'
import { computed } from 'vue'
import type { Row } from '@/data/types/datatable'

export function useContractStatus(
	status: number | null | undefined,
	subStatus?: string | null | undefined
) {
	const icon = computed(() => {
		if (!status) {
			return statusMap['action-required'].icon
		}

		const key =
			Object.keys(statusMap).find((key) => statusMap[key].code === status) ??
			'action-required'
		return statusMap[key].icon
	})

	const label = computed(() => {
		if (!status) {
			return statusMap['action-required'].label
		}

		const key =
			Object.keys(statusMap).find((key) => statusMap[key].code === status) ??
			'action-required'
		return statusMap[key].label
	})

	const colour = computed(() => {
		switch (subStatus) {
			case 'pick-warning':
				return 'purple'
			case 'in-progress-full':
				return '#002955'
			case 'in-progress-warning':
				return '#FFBF00'
			case 'in-progress-complete':
				return '#89b550'
			case 'load':
				return '#6AB2E7'
			default:
				return undefined
		}
	})

	const statusOptions = computed(() => {
		return Object.values(statusMap).map((status: Status) => ({
			value: String(status.code),
			label: status.label
		}))
	})

	return { icon, label, colour, statusOptions }
}

export function useItemStatus(status: number | null | undefined) {
	const icon = computed(() => {
		switch (status) {
			case 0:
				return 'action-required'
			case 1:
				return 'in-progress'
			case 2:
				return 'warning'
			case 3:
				return 'complete'
			case 4:
				return 'skipped'
			case 5:
				return 'in-transit'
			case 6:
				return 'delivered'
			case 7: // load in progress
				return 'in-progress'
			case 8: // load assigned
				return 'assigned'
			case 9: // load warning
				return 'warning'
			case 10: // load complete
				return 'complete'
			default:
				return 'in-progress'
		}
	})

	const label = computed(() => {
		switch (status) {
			case 0:
				return 'Action'
			case 1:
				return 'In Progress'
			case 2:
				return 'Warning'
			case 3:
				return 'Complete'
			case 4:
				return 'Skipped'
			case 5:
				return 'In Transit'
			case 6:
				return 'Delivered'
			case 7:
				return 'Load In Progress'
			case 8:
				return 'Load Assigned'
			case 9:
				return 'Load Warning'
			case 10:
				return 'Load Complete'
			default:
				return 'In Progress'
		}
	})

	return { icon, label }
}

export function getStatusSvg(path: string) {
	return fetch(path)
		.then((response) => {
			if (!response.ok) {
				throw new Error('Network response failed for getStatusSvg')
			}
			return response.text()
		})
		.catch((error) => {
			console.error('Error occurred in getStatusSvg: ', error)
		})
}

export function getStatusIcon(item: Row, key: StatusKey | number) {
	let subStatus = item.status[0].sub_status ?? undefined

	// Pick and load merge handler where load supersedes pick
	if (key === 'pick_status' && item.status[0].load_status) {
		key = 'load_status'
		subStatus = 'load'
	}

	const { label, icon, colour } = useContractStatus(item.status[0][key] ?? 0, subStatus)

	return {
		label: label.value,
		icon: icon.value,
		colour: colour.value
	}
}
